import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useCopyToClipboard } from 'app/hooks/useCopyToClipboard'
import { LocationType } from 'app/types/entities'
import type { TabType } from 'app/components/Tabs'
import { logError } from 'lib/logging'
import { useLocationStore } from 'app/stores/StoreContext'

export type UseAvailabilityLocationReturn = {
  title: string
  tabs: TabType[]
  onCopyPreview: () => void
  onCopyLink: () => void
}

export const getTabs = (isExternalSlots?: boolean): TabType[] => [
  { name: `Settings`, href: `settings` },
  ...(!isExternalSlots ? [{ name: `Availability`, href: `availability` }] : []),
  { name: `Appointment Types`, href: `appointment-types` },
  ...(isExternalSlots ? [{ name: `Providers`, href: `providers` }] : []),
  ...(!isExternalSlots ? [{ name: `Calendar`, href: `calendar` }] : []),
]

export function useAvailabilityLocation(): UseAvailabilityLocationReturn {
  const { locationId } = useParams()
  const copyToClipboard = useCopyToClipboard()
  const [location, setLocation] = useState<LocationType>()
  const locationStore = useLocationStore()

  useEffect(() => {
    if (!locationId) return
    locationStore
      .fetchOne(locationId)
      .then((result) => {
        const data = result.data as any
        if (data) setLocation(data.location ?? data)
      })
      .catch((err) => logError(`Error fetching location`, err))
  }, [locationId])

  const isExternalSlots = !!location?.carestackId || !!location?.ascendLocationId
  /** Deferred to avoid accessing `window` during SSR — only called from click handlers (client-side). */
  const getSchedulingUrl = () => `${window.location.origin}/scheduling/${location?.id ?? locationId}`

  return {
    title: location?.name ?? ``,
    tabs: getTabs(isExternalSlots),
    onCopyPreview: () =>
      copyToClipboard(
        `${getSchedulingUrl()}?preview=true`,
        `Copied preview link to clipboard`,
        `copy-preview-to-clipboard`,
      ),
    onCopyLink: () => copyToClipboard(getSchedulingUrl(), `Copied to clipboard`, `copy-to-clipboard`),
  }
}
